import React, { useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import Layout from '@/components/layout/Layout'
import ServicesManager from '@/components/admin/ServicesManager'
import ProjectsManager from '@/components/admin/ProjectsManager'
import TestimonialsManager from '@/components/admin/TestimonialsManager'
import TeamManager from '@/components/admin/TeamManager'
import TechStackManager from '@/components/admin/TechStackManager'
import ContentManager from '@/components/admin/ContentManager'
import ClientLogosManager from '@/components/admin/ClientLogosManager'
import ProcessStepsManager from '@/components/admin/ProcessStepsManager'
import ContactDetailsManager from '@/components/admin/ContactDetailsManager'

const Admin = () => {
  const { user, signIn, signOut, loading } = useAuth()
  const { toast } = useToast()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [activeView, setActiveView] = useState<string>('dashboard')

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    
    try {
      await signIn(email, password)
      toast({
        title: "Success",
        description: "Signed in successfully!",
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to sign in. Please check your credentials.",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut()
      setActiveView('dashboard')
      toast({
        title: "Success",
        description: "Signed out successfully!",
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to sign out.",
        variant: "destructive",
      })
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div>Loading...</div>
        </div>
      </Layout>
    )
  }

  if (user) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 py-12">
          <div className="container-custom">
            <div className="flex justify-between items-center mb-8">
              <h1 className="text-3xl font-bold">Admin Dashboard</h1>
              <div className="flex gap-4">
                <Button 
                  variant={activeView === 'dashboard' ? 'default' : 'outline'}
                  onClick={() => setActiveView('dashboard')}
                >
                  Dashboard
                </Button>
                <Button onClick={handleSignOut} variant="outline">
                  Sign Out
                </Button>
              </div>
            </div>
            
            {activeView === 'dashboard' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Services</CardTitle>
                    <CardDescription>Manage your services content</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      className="w-full"
                      onClick={() => setActiveView('services')}
                    >
                      Manage Services
                    </Button>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Projects</CardTitle>
                    <CardDescription>Manage your project portfolio</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      className="w-full"
                      onClick={() => setActiveView('projects')}
                    >
                      Manage Projects
                    </Button>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Client Logos</CardTitle>
                    <CardDescription>Manage homepage client logos in the carousel </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      className="w-full"
                      onClick={() => setActiveView('client-logos')}
                    >
                      Manage Client Logos
                    </Button>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Testimonials</CardTitle>
                    <CardDescription>Manage client testimonials</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      className="w-full"
                      onClick={() => setActiveView('testimonials')}
                    >
                      Manage Testimonials
                    </Button>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Team</CardTitle>
                    <CardDescription>Manage team member information</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      className="w-full"
                      onClick={() => setActiveView('team')}
                    >
                      Manage Team
                    </Button>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Tech Stack</CardTitle>
                    <CardDescription>Manage technology stack categories</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      className="w-full"
                      onClick={() => setActiveView('techstack')}
                    >
                      Manage Tech Stack
                    </Button>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Process Steps</CardTitle>
                    <CardDescription>Manage the "Our Process" section steps</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      className="w-full"
                      onClick={() => setActiveView('process-steps')}
                    >
                      Manage Process Steps
                    </Button>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Client and Banner</CardTitle>
                    <CardDescription>Manage client and banner content</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      className="w-full"
                      onClick={() => setActiveView('content')}
                    >
                      Manage Content
                    </Button>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Contact Details</CardTitle>
                    <CardDescription>Manage contact information for pages and footer</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      className="w-full"
                      onClick={() => setActiveView('contact-details')}
                    >
                      Manage Contact Details
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeView === 'services' && <ServicesManager />}
            {activeView === 'projects' && <ProjectsManager />}
            {activeView === 'client-logos' && <ClientLogosManager />}
            {activeView === 'testimonials' && <TestimonialsManager />}
            {activeView === 'team' && <TeamManager />}
            {activeView === 'techstack' && <TechStackManager />}
            {activeView === 'process-steps' && <ProcessStepsManager />}
            {activeView === 'content' && <ContentManager />}
            {activeView === 'contact-details' && <ContactDetailsManager />}
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Admin Login</CardTitle>
            <CardDescription>
              Sign in to access the GoalTec admin dashboard
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSignIn} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? 'Signing In...' : 'Sign In'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  )
}

export default Admin
